function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error('The optimized photo could not be read.'));
    reader.onload = () => resolve(String(reader.result || ''));
    reader.readAsDataURL(blob);
  });
}

function canvasToBlob(canvas, type, quality) {
  return new Promise(resolve => canvas.toBlob(resolve, type, quality));
}

async function decodeImage(file) {
  if (typeof createImageBitmap === 'function') {
    try {
      const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' });
      return {
        width: bitmap.width,
        height: bitmap.height,
        draw(context, width, height) { context.drawImage(bitmap, 0, 0, width, height); },
        close() { bitmap.close(); },
      };
    } catch {
      // The Image fallback produces a clearer error for formats this browser cannot decode.
    }
  }

  const objectUrl = URL.createObjectURL(file);
  try {
    const image = await new Promise((resolve, reject) => {
      const element = new Image();
      element.onload = () => resolve(element);
      element.onerror = () => reject(new Error('This browser could not decode that photo. Convert HEIC files to JPEG if needed.'));
      element.src = objectUrl;
    });
    return {
      width: image.naturalWidth,
      height: image.naturalHeight,
      draw(context, width, height) { context.drawImage(image, 0, 0, width, height); },
      close() { URL.revokeObjectURL(objectUrl); },
    };
  } catch (error) {
    URL.revokeObjectURL(objectUrl);
    throw error;
  }
}

export async function fileToOptimizedImage(file, {
  maxDimension = 1600,
  minDimension = 900,
  targetBytes = 900 * 1024,
} = {}) {
  if (!file || (file.type && !file.type.startsWith('image/'))) {
    throw new Error('Choose an image file.');
  }

  const source = await decodeImage(file);
  try {
    if (!source.width || !source.height) throw new Error('The selected photo has no readable dimensions.');
    const canvas = document.createElement('canvas');
    const supportsWebp = canvas.toDataURL('image/webp').startsWith('data:image/webp');
    const type = supportsWebp ? 'image/webp' : 'image/jpeg';
    let longEdge = Math.min(maxDimension, Math.max(source.width, source.height));
    let best = null;

    while (longEdge >= Math.min(minDimension, Math.max(source.width, source.height))) {
      const scale = Math.min(1, longEdge / Math.max(source.width, source.height));
      canvas.width = Math.max(1, Math.round(source.width * scale));
      canvas.height = Math.max(1, Math.round(source.height * scale));
      const context = canvas.getContext('2d', { alpha: type === 'image/webp' });
      context.imageSmoothingEnabled = true;
      context.imageSmoothingQuality = 'high';
      context.clearRect(0, 0, canvas.width, canvas.height);
      source.draw(context, canvas.width, canvas.height);

      for (const quality of [0.84, 0.76, 0.68, 0.60, 0.52]) {
        const blob = await canvasToBlob(canvas, type, quality);
        if (!blob) continue;
        best = blob;
        if (blob.size <= targetBytes) return blobToDataUrl(blob);
      }

      if (scale === 1 && Math.max(source.width, source.height) <= minDimension) break;
      longEdge = Math.floor(longEdge * 0.82);
    }

    if (!best) throw new Error('This browser could not compress the selected photo.');
    return blobToDataUrl(best);
  } finally {
    source.close();
  }
}
